import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoneyDisplay } from '../MoneyDisplay';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Info } from 'lucide-react';

export interface WeeklyDetails {
  // Recebíveis a vencer
  recebiveisAVencer: Array<{
    cliente: string;
    valor: number;
    vencimento: string;
  }>;
  // Recebíveis em atraso
  recebiveisEmAtraso: Array<{
    cliente: string;
    valor: number;
    diasAtraso: number;
  }>;
  // Despesas fixas
  despesasFixas: Array<{
    nome: string;
    valor: number;
    moeda: string;
    valorUSD: number;
  }>;
  // Funcionários que pagam nesta semana
  funcionarios: Array<{
    nome: string;
    cargo: string;
    custoMensal: number;
    tipoPagamento: string;
    diaPagamento: string;
    custoSemana: number;
  }>;
  // Detalhes de novas vendas com parcelamento
  novasVendas: {
    metaMensal: number;
    metaSemanal: number;
    distribuicaoMes: number;           // % do contrato que entra neste mês
    taxaRecebimento: number;           // taxa aplicada (curva de recebimento)
    valorProjetado: number;            // valor final projetado
    fonteCalculo: 'dre_projetado' | 'media_historica';
    // Breakdown por origem
    breakdown: {
      dealsFechadosEsteMes: number;    // Primeira parcela de deals novos
      parcelasDealsAnteriores: number; // Parcelas de deals de meses anteriores
    };
  };
  // Métricas aplicadas
  metricas: {
    curvaRecebimento: { semana0: number; semana1: number; semana2: number; semana3: number };
    taxaRecebimentoAtraso: number;
    taxaCambio: number;
    vendaNovaBase: number;
    usandoDreProjetado: boolean;
    impostoAliquota: number;
    parcelamento: {
      mediaParcelasPorDeal: number;
      distribuicaoRecebimento: { mes0: number; mes1: number; mes2: number; mes3: number; mes4: number; mes5: number; mes6: number };
    };
  };
  // Totais originais
  totais: {
    totalAVencerOriginal: number;
    totalAVencerProjetado: number;
    totalAtrasoOriginal: number;
    totalAtrasoProjetado: number;
    totalDespesasFixasMensal: number;
    totalDespesasFixasSemanal: number;
    totalFolhaMensal: number;
    totalFolhaSemanal: number;
    totalImpostos: number;
  };
}

type DetailCategory = 'aVencer' | 'atraso' | 'novasVendas' | 'folha' | 'fixas' | 'impostos' | 'saldoInicial' | 'saldoFinal';

interface CashFlowDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  weekLabel: string;
  weekIndex: number;
  category: DetailCategory;
  details: WeeklyDetails | null;
  value: number;
}

const categoryLabels: Record<DetailCategory, string> = {
  aVencer: 'Recebíveis A Vencer',
  atraso: 'Recebíveis Em Atraso',
  novasVendas: 'Vendas Novas',
  folha: 'Folha de Pagamento',
  fixas: 'Despesas Fixas',
  impostos: 'Impostos',
  saldoInicial: 'Saldo Inicial',
  saldoFinal: 'Saldo Final',
};

export function CashFlowDetailsModal({
  isOpen,
  onClose,
  weekLabel,
  weekIndex,
  category,
  details,
  value,
}: CashFlowDetailsModalProps) {
  if (!details) return null;

  const renderContent = () => {
    switch (category) {
      case 'aVencer':
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              Aplicando curva de recebimento baseada em histórico real
            </div>
            
            {details.recebiveisAVencer.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Recebíveis que vencem nesta semana:</h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-2 px-3">Cliente</th>
                        <th className="text-right py-2 px-3">Vencimento</th>
                        <th className="text-right py-2 px-3">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.recebiveisAVencer.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3 truncate max-w-[200px]">{r.cliente || 'N/A'}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">{r.vencimento}</td>
                          <td className="py-2 px-3 text-right text-emerald-600">
                            <MoneyDisplay value={r.valor} showCents={false} size="sm" />
                          </td>
                        </tr>
                      ))}
                      {details.recebiveisAVencer.length > 10 && (
                        <tr className="border-t bg-muted/30">
                          <td colSpan={3} className="py-2 px-3 text-center text-muted-foreground text-sm">
                            ... e mais {details.recebiveisAVencer.length - 10} recebíveis
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Nenhum recebível vence nesta semana.</p>
            )}

            <Separator />

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Fórmula aplicada:</h4>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono space-y-1">
                <p>Semana {weekIndex + 1}: {(details.metricas.curvaRecebimento.semana0 * 100).toFixed(0)}% do que vence esta semana</p>
                {weekIndex > 0 && <p>+ {(details.metricas.curvaRecebimento.semana1 * 100).toFixed(0)}% do que venceu na semana anterior</p>}
                {weekIndex > 1 && <p>+ {(details.metricas.curvaRecebimento.semana2 * 100).toFixed(0)}% do que venceu há 2 semanas</p>}
                {weekIndex > 2 && <p>+ {(details.metricas.curvaRecebimento.semana3 * 100).toFixed(0)}% do que venceu há 3 semanas</p>}
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <span className="font-medium">Total Original:</span>
              <MoneyDisplay value={details.totais.totalAVencerOriginal} showCents={false} size="sm" />
            </div>
            <div className="flex justify-between items-center text-emerald-600 font-semibold">
              <span>Total Projetado (após curva):</span>
              <MoneyDisplay value={value} showCents={false} size="sm" />
            </div>
          </div>
        );

      case 'atraso':
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              Taxa de recuperação: {(details.metricas.taxaRecebimentoAtraso * 100).toFixed(0)}%
            </div>

            {details.recebiveisEmAtraso.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Recebíveis em atraso:</h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-2 px-3">Cliente</th>
                        <th className="text-right py-2 px-3">Dias Atraso</th>
                        <th className="text-right py-2 px-3">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.recebiveisEmAtraso.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3 truncate max-w-[200px]">{r.cliente || 'N/A'}</td>
                          <td className="py-2 px-3 text-right">
                            <Badge variant={r.diasAtraso > 30 ? 'destructive' : 'outline'}>
                              {r.diasAtraso}d
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-right text-amber-600">
                            <MoneyDisplay value={r.valor} showCents={false} size="sm" />
                          </td>
                        </tr>
                      ))}
                      {details.recebiveisEmAtraso.length > 10 && (
                        <tr className="border-t bg-muted/30">
                          <td colSpan={3} className="py-2 px-3 text-center text-muted-foreground text-sm">
                            ... e mais {details.recebiveisEmAtraso.length - 10} recebíveis
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Nenhum recebível em atraso.</p>
            )}

            <Separator />

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Fórmula aplicada:</h4>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono">
                <p>(Total em atraso × {(details.metricas.taxaRecebimentoAtraso * 100).toFixed(0)}%) ÷ 4 semanas</p>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <span className="font-medium">Total em Atraso:</span>
              <MoneyDisplay value={details.totais.totalAtrasoOriginal} showCents={false} size="sm" />
            </div>
            <div className="flex justify-between items-center text-emerald-600 font-semibold">
              <span>Projetado (por semana):</span>
              <MoneyDisplay value={value} showCents={false} size="sm" />
            </div>
          </div>
        );

      case 'novasVendas':
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              {details.novasVendas.fonteCalculo === 'dre_projetado' 
                ? 'Usando DRE Projetado como base' 
                : 'Usando média histórica de vendas'}
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Cálculo com Parcelamento Real:</h4>
              
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b bg-muted/30">
                      <td className="py-2 px-3 font-medium">Meta Mensal de Vendas</td>
                      <td className="py-2 px-3 text-right">
                        <MoneyDisplay value={details.novasVendas.metaMensal} showCents={false} size="sm" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3">÷ 4 semanas = Meta Semanal</td>
                      <td className="py-2 px-3 text-right">
                        <MoneyDisplay value={details.novasVendas.metaSemanal} showCents={false} size="sm" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3">× {(details.novasVendas.distribuicaoMes * 100).toFixed(0)}% (1ª parcela)</td>
                      <td className="py-2 px-3 text-right text-muted-foreground">
                        <MoneyDisplay value={details.novasVendas.metaSemanal * details.novasVendas.distribuicaoMes} showCents={false} size="sm" />
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-3">× {(details.novasVendas.taxaRecebimento * 100).toFixed(0)}% (taxa pagamento)</td>
                      <td className="py-2 px-3 text-right text-emerald-600">
                        <MoneyDisplay value={details.novasVendas.breakdown.dealsFechadosEsteMes} showCents={false} size="sm" />
                      </td>
                    </tr>
                    {details.novasVendas.breakdown.parcelasDealsAnteriores > 0 && (
                      <tr className="border-b">
                        <td className="py-2 px-3">+ Parcelas atrasadas de vendas anteriores</td>
                        <td className="py-2 px-3 text-right text-emerald-600">
                          <MoneyDisplay value={details.novasVendas.breakdown.parcelasDealsAnteriores} showCents={false} size="sm" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Separator />

            <div className="bg-muted/30 rounded-md p-3 text-xs space-y-1">
              <p className="font-medium">💡 Por que não entra 100% da meta?</p>
              <p>Contratos são parcelados em média {details.metricas.parcelamento.mediaParcelasPorDeal.toFixed(0)} vezes.</p>
              <p>Apenas ~{(details.novasVendas.distribuicaoMes * 100).toFixed(0)}% do valor entra no mês do fechamento.</p>
              <p>O restante ({(100 - details.novasVendas.distribuicaoMes * 100).toFixed(0)}%) entra nos meses seguintes.</p>
            </div>

            <div className="flex justify-between items-center text-emerald-600 font-semibold pt-2">
              <span>Total Projetado:</span>
              <MoneyDisplay value={value} showCents={false} size="sm" />
            </div>
          </div>
        );

      case 'folha':
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              Folha paga na última semana do mês (dias 25-31 ou 1-5)
            </div>

            {details.funcionarios.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Funcionários que recebem nesta semana ({details.funcionarios.length}):</h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-2 px-3">Nome</th>
                        <th className="text-left py-2 px-3">Tipo</th>
                        <th className="text-left py-2 px-3">Dia</th>
                        <th className="text-right py-2 px-3">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.funcionarios.slice(0, 15).map((f, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3 truncate max-w-[120px]">{f.nome}</td>
                          <td className="py-2 px-3">
                            <Badge variant={f.tipoPagamento === 'Semanal' ? 'secondary' : 'outline'} className="text-xs">
                              {f.tipoPagamento}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-muted-foreground text-xs">{f.diaPagamento}</td>
                          <td className="py-2 px-3 text-right text-destructive">
                            <MoneyDisplay value={f.custoSemana} showCents={false} size="sm" />
                          </td>
                        </tr>
                      ))}
                      {details.funcionarios.length > 15 && (
                        <tr className="border-t bg-muted/30">
                          <td colSpan={4} className="py-2 px-3 text-center text-muted-foreground text-sm">
                            ... e mais {details.funcionarios.length - 15} funcionários
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">Nenhum pagamento de folha nesta semana.</p>
                <p className="text-xs mt-1">Os pagamentos semanais caem nos dias configurados de cada funcionário.</p>
              </div>
            )}

            <Separator />

            <div className="flex justify-between items-center">
              <span className="font-medium">Total Mensal (referência):</span>
              <MoneyDisplay value={details.totais.totalFolhaMensal} showCents={false} size="sm" />
            </div>
            <div className="flex justify-between items-center text-destructive font-semibold">
              <span>Total Nesta Semana:</span>
              <MoneyDisplay value={value} showCents={false} size="sm" />
            </div>
            {details.funcionarios.length > 0 && (
              <div className="text-xs text-muted-foreground">
                Soma dos pagamentos: {details.funcionarios.map(f => f.nome.split(' ')[0]).join(', ')}
              </div>
            )}
          </div>
        );

      case 'fixas':
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              Despesas fixas mensais distribuídas em 4 semanas | Câmbio: {details.metricas.taxaCambio.toFixed(2)}
            </div>

            {details.despesasFixas.length > 0 ? (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Despesas Recorrentes ({details.despesasFixas.length}):</h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-2 px-3">Descrição</th>
                        <th className="text-right py-2 px-3">Valor Original</th>
                        <th className="text-right py-2 px-3">USD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.despesasFixas.map((d, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3 truncate max-w-[180px]">{d.nome}</td>
                          <td className="py-2 px-3 text-right text-muted-foreground">
                            {d.moeda === 'BRL' ? 'R$' : '$'}{d.valor.toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-right text-destructive">
                            <MoneyDisplay value={d.valorUSD} showCents={false} size="sm" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Nenhuma despesa fixa cadastrada.</p>
            )}

            <Separator />

            <div className="flex justify-between items-center">
              <span className="font-medium">Total Mensal (USD):</span>
              <MoneyDisplay value={details.totais.totalDespesasFixasMensal} showCents={false} size="sm" />
            </div>
            <div className="flex justify-between items-center text-destructive font-semibold">
              <span>Por Semana (÷4):</span>
              <MoneyDisplay value={value} showCents={false} size="sm" />
            </div>
          </div>
        );

      case 'impostos':
        return (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4" />
              Impostos estimados como {(details.metricas.impostoAliquota * 100).toFixed(0)}% da receita mensal
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm">Cálculo:</h4>
              <div className="bg-muted/30 rounded-md p-3 text-xs font-mono space-y-1">
                <p>Vencimento estimado: dia 20 de cada mês</p>
                <p>Receita mensal projetada × {(details.metricas.impostoAliquota * 100).toFixed(0)}%</p>
              </div>
            </div>

            <div className="flex justify-between items-center text-destructive font-semibold">
              <span>Nesta Semana:</span>
              <MoneyDisplay value={value} showCents={false} size="sm" />
            </div>
          </div>
        );

      default:
        return <p className="text-muted-foreground">Detalhes não disponíveis para esta categoria.</p>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{categoryLabels[category]}</span>
            <Badge variant="outline">{weekLabel}</Badge>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-4">
          {renderContent()}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
